import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Loader from "../loader/Loader";
import { productsUrl, searchUrl } from "../../api";
import { toast } from "react-toastify";
import "./Products.css"; // We'll create this or use inline styles for now

const Products = () => {
  const [bookData, setBookData] = useState([]);
  const [currPage, setCurrPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // Assuming backend sends this or we just check if data < limit
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const postsPerPage = 20; // manageable batch size

  const fetchProducts = async (page, search = "") => {
    setLoading(true);
    try {
      let url = `${productsUrl}?pageNo=${page}&pageSize=${postsPerPage}`;
      if (search) {
        // If search is active, we might use a different endpoint or just filter
        // The previous code used searchUrl which returns a list directly
        url = `${searchUrl}${search}`;
      }

      const res = await fetch(url);
      const data = await res.json();

      if (search) {
        // Search usually returns all matches or specific format
        setBookData(data || []);
        setHasMore(false); // Disable load more for search results mainly because search API might not be paginated the same way
      } else {
        if (page === 1) {
          setBookData(data.data);
        } else {
          setBookData(prev => [...prev, ...data.data]);
        }

        if (data.data.length < postsPerPage) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchTerm) {
      fetchProducts(currPage);
    }
  }, [currPage, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      fetchProducts(1, searchTerm);
    } else {
      setCurrPage(1);
      setHasMore(true);
      fetchProducts(1);
    }
  };

  const handleLoadMore = () => {
    setCurrPage(prev => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrPage(1);
    setHasMore(true);
  // fetchProducts(1) will trigger due to effect
  };

  return (
    <div className="content-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="text-white">Product Management</h3>
        <Link to="/products/add" className="btn btn-success">
          <i className="bi bi-plus-lg me-2"></i>Create Product
        </Link>
      </div>

      {/* Search Bar */}
      <div className="card bg-dark text-white mb-4 border-secondary search-card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="text"
              className="form-control bg-dark text-white border-secondary"
              placeholder="Search by title, author, or tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">Search</button>
            {searchTerm && (
              <button type="button" className="btn btn-outline-secondary" onClick={handleClearSearch}>Clear</button>
            )}
          </form>
        </div>
      </div>

      {/* Product Table */}
      <div className="card bg-dark text-white border-secondary table-responsive">
        <table className="table table-dark table-hover mb-0 align-middle">
          <thead>
            <tr>
              <th scope="col" style={{ width: '80px' }}>Image</th>
              <th scope="col">Title</th>
              <th scope="col">Author</th>
              <th scope="col">Price</th>
              <th scope="col">Drop</th>
              <th scope="col" className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookData.length > 0 ? (
              bookData.map((item) => (
                <tr key={item._id}>
                  <td>
                    <img
                      src={item.coverImage?.startsWith('http') ? item.coverImage : `https://backend.cdp3d.com${item.coverImage}`}
                      alt={item.title}
                      className="rounded"
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                        </td>
                        <td>
                          <div className="fw-bold">{item.title}</div>
                          <small className="text-muted">{item._id}</small>
                        </td>
                        <td>{item.additionalDetails?.author || "N/A"}</td>
                        <td>₹{item.currentPrice}</td>
                        <td>
                          {item.dropId ? <span className="badge bg-info">Assigned</span> : <span className="badge bg-secondary">None</span>}
                        </td>
                        <td className="text-end">
                          <Link to={`/products/edit/${item._id}`} className="btn btn-sm btn-outline-primary me-2">
                            <i className="bi bi-pencil"></i> Edit
                          </Link>
                          <Link to={`/products/${item._id}`} className="btn btn-sm btn-outline-secondary">
                            <i className="bi bi-eye"></i> View
                          </Link>
                        </td>
                      </tr>
                    ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  {loading ? <Loader /> : "No products found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination / Load More */}
      {!searchTerm && hasMore && (
        <div className="text-center mt-4">
          <button 
            className="btn btn-outline-light px-5"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More Products"}
          </button>
        </div>
      )}

      {/* Inline Styles for Glass/Dark Theme adjustments specific to this page if needed */}
      <style>{`
        .search-card {
            background-color: rgba(30, 30, 30, 0.6) !important;
            backdrop-filter: blur(10px);
        }
        .table-dark {
            --bs-table-bg: transparent;
            --bs-table-striped-bg: rgba(255, 255, 255, 0.05);
            --bs-table-hover-bg: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Products;
